import { create } from 'zustand';
import type { TenantPublicConfig } from '../types/tenant';
import { fetchTenantConfigForHost } from '../services/tenantApi';

interface TenantStoreState {
  publicConfig: TenantPublicConfig | null;
  loading: boolean;
  error: string | null;

  resolveForCurrentHost: () => Promise<void>;
  setPublicConfig: (config: TenantPublicConfig | null) => void;
  applyBrandingToDocument: () => void;
}

const DEFAULT_PALETTE = {
  primary: '#2D7DD2',
  accent: '#F6AE2D',
};

function setCssVar(name: string, value: string | null | undefined) {
  if (value && value.trim()) {
    document.documentElement.style.setProperty(name, value);
  } else {
    document.documentElement.style.removeProperty(name);
  }
}

// Coalesces concurrent calls (e.g. StrictMode double-invocation) into a
// single network fetch. Cleared once the request settles.
let resolveInFlight: Promise<void> | null = null;

export const useTenantStore = create<TenantStoreState>((set, get) => ({
  publicConfig: null,
  loading: false,
  error: null,

  async resolveForCurrentHost() {
    if (resolveInFlight) return resolveInFlight;

    resolveInFlight = (async () => {
      set({ loading: true, error: null });
      try {
        const config = await fetchTenantConfigForHost();
        set({ publicConfig: config, loading: false });
        get().applyBrandingToDocument();
      } catch (err) {
        console.warn('[tenant-store] public config resolution failed', err);
        set({
          loading: false,
          error:
            err instanceof Error
              ? err.message
              : 'Failed to resolve tenant configuration',
        });
      }
    })().finally(() => {
      resolveInFlight = null;
    });

    return resolveInFlight;
  },

  setPublicConfig(config) {
    set({ publicConfig: config });
    get().applyBrandingToDocument();
  },

  applyBrandingToDocument() {
    const cfg = get().publicConfig;
    const primary = cfg?.branding?.primaryColor || DEFAULT_PALETTE.primary;
    const accent = cfg?.branding?.accentColor || DEFAULT_PALETTE.accent;
    setCssVar('--tenant-primary', primary);
    setCssVar('--tenant-accent', accent);

    // Favicon + page title swap. Safe to call repeatedly.
    const favicon = cfg?.branding?.faviconUrl;
    if (favicon) {
      const link =
        document.querySelector<HTMLLinkElement>("link[rel~='icon']") ??
        (() => {
          const l = document.createElement('link');
          l.rel = 'icon';
          document.head.appendChild(l);
          return l;
        })();
      link.href = favicon;
    }
    const displayName = cfg?.branding?.displayName || cfg?.displayName;
    if (displayName) {
      document.title = `${displayName} · MAIE`;
    }
  },
}));
